import React, { useCallback, useEffect, useState, lazy, Suspense } from 'react';
import {
  ShieldAlert, Search, CheckCircle2, XCircle, Clock, FileText, History,
  Loader2, Inbox, AlarmClock, GraduationCap,
} from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

const HRISTraining = lazy(() => import('./HRISTraining'));
const HRISDocumentReview = lazy(() => import('./HRISDocumentReview'));

const STATUS_META = {
  valid:         { label: 'Valid',        cls: 'bg-emerald-100 text-emerald-800' },
  expiring_30d:  { label: 'Expiring 30d', cls: 'bg-amber-100 text-amber-800' },
  expiring_7d:   { label: 'Expiring 7d',  cls: 'bg-orange-100 text-orange-800' },
  expired:       { label: 'Expired',      cls: 'bg-red-100 text-red-800' },
  missing:       { label: 'Missing',      cls: 'bg-slate-100 text-slate-600' },
  pending_review:{ label: 'In review',    cls: 'bg-blue-100 text-blue-800' },
  overridden:    { label: 'Overridden',   cls: 'bg-violet-100 text-violet-800' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const TABS = [
  { id: 'queue',    label: 'Review Queue',   icon: Inbox },
  { id: 'expiring', label: 'Expiring Soon',  icon: AlarmClock },
  { id: 'lookup',   label: 'Worker Lookup',  icon: Search },
  { id: 'training', label: 'Training',       icon: GraduationCap },
  { id: 'audit',    label: 'Audit Log',      icon: History },
];

/**
 * Admin HRIS Console — tabbed workspace covering the queue of docs
 * awaiting review, expiring credentials across the whole platform,
 * per-worker deep-dive, and the tamper-evident audit log.
 */
export default function AdminHrisConsole() {
  const [tab, setTab] = useState('queue');

  return (
    <div className="space-y-4" data-testid="admin-hris-console">
      <div>
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-red-600" /> HRIS Console
        </h2>
        <p className="text-xs text-slate-500">
          Full HR access for platform admins. Every read + action is logged to the audit trail.
        </p>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                isActive
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
              data-testid={`admin-hris-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="pt-2">
        {tab === 'queue'    && <Suspense fallback={null}><HRISDocumentReview /></Suspense>}
        {tab === 'expiring' && <ExpiringTab />}
        {tab === 'lookup'   && <WorkerLookupTab />}
        {tab === 'training' && (
          <Suspense fallback={<div className="text-center py-8 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
            <HRISTraining />
          </Suspense>
        )}
        {tab === 'audit'    && <AuditTab />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Review Queue
// ---------------------------------------------------------------------
export function ReviewQueueTab() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiCall('/api/hris/admin/review-queue?limit=100');
      setQueue(r.queue || []);
    } catch (e) {
      toast.error(e?.message || 'Could not load queue');
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const review = async (row, decision) => {
    setBusy(`${row._id}:${decision}`);
    try {
      await apiCall(
        `/api/hris/admin/workers/${encodeURIComponent(row.worker_id)}/documents/${encodeURIComponent(row._id)}/review`,
        { method: 'POST', body: JSON.stringify({ decision, note: '' }) },
      );
      toast.success(`Document ${decision}d`);
      await load();
    } catch (e) {
      toast.error(e?.message || 'Review failed');
    }
    setBusy(null);
  };

  if (loading && queue.length === 0) {
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }
  if (queue.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500" data-testid="admin-hris-queue-empty">
        Inbox zero — no documents waiting for review right now.
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Inbox className="w-4 h-4 text-slate-500" />
          <h3 className="font-semibold text-slate-900 text-sm">Documents awaiting review ({queue.length})</h3>
        </div>
        <span className="text-[11px] text-slate-500">Oldest first</span>
      </div>
      <ul className="divide-y divide-slate-100">
        {queue.map((row) => (
          <li key={row._id} className="px-4 py-3" data-testid={`admin-hris-queue-${row._id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">
                  {row.display_name}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {row.worker_name} · {row.category} · {row.subtype}
                  {row.expires_at && <> · expires {fmtDate(row.expires_at)}</>}
                  <> · submitted {fmtDate(row.updated_at)}</>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => review(row, 'approve')}
                  disabled={!!busy}
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  data-testid={`admin-hris-queue-approve-${row._id}`}
                >
                  <CheckCircle2 className="w-3 h-3" /> Approve
                </button>
                <button
                  onClick={() => review(row, 'reject')}
                  disabled={!!busy}
                  className="inline-flex items-center gap-1 rounded-md bg-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-300 disabled:opacity-50"
                  data-testid={`admin-hris-queue-reject-${row._id}`}
                >
                  <XCircle className="w-3 h-3" /> Reject
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------
// Expiring soon
// ---------------------------------------------------------------------
export function ExpiringTab() {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ expired: 0, expiring_7d: 0, expiring_30d: 0 });
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiCall(`/api/hris/admin/expiring?days=${days}`);
      setRows(r.documents || []);
      setCounts(r.counts || { expired: 0, expiring_7d: 0, expiring_30d: 0 });
    } catch (e) {
      toast.error(e?.message || 'Could not load expiring documents');
    }
    setLoading(false);
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await apiCall('/api/hris/admin/scan-expirations', { method: 'POST' });
      const totalFired = (r.fired?.expired || 0) + (r.fired?.expiring_7d || 0) + (r.fired?.expiring_30d || 0);
      setLastScan({ ...r, total: totalFired });
      toast.success(
        totalFired === 0
          ? `Scanned ${r.scanned} docs · no new notifications needed`
          : `Notified workers on ${totalFired} credential(s)`,
      );
      await load();
    } catch (e) {
      toast.error(e?.message || 'Scan failed');
    }
    setScanning(false);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Expired" value={counts.expired} cls="text-red-700 bg-red-50 border-red-200" testid="admin-hris-stat-expired" />
        <StatCard label="Expiring 7d" value={counts.expiring_7d} cls="text-orange-700 bg-orange-50 border-orange-200" testid="admin-hris-stat-7d" />
        <StatCard label="Expiring 30d" value={counts.expiring_30d} cls="text-amber-700 bg-amber-50 border-amber-200" testid="admin-hris-stat-30d" />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-xs text-slate-600">Window:</label>
        {[7, 30, 60, 90].map((w) => (
          <button
            key={w}
            onClick={() => setDays(w)}
            className={`px-2 py-1 rounded-md text-xs font-semibold border ${
              days === w
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
            data-testid={`admin-hris-expiring-window-${w}`}
          >
            {w} days
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={runScan}
          disabled={scanning}
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          data-testid="admin-hris-scan-expirations"
          title="Fire in-app notifications for docs crossing 30d/7d/expired thresholds. Idempotent — safe to re-run."
        >
          {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlarmClock className="w-3.5 h-3.5" />}
          {scanning ? 'Scanning…' : 'Run scan · notify workers'}
        </button>
      </div>
      {lastScan && (
        <div
          className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
          data-testid="admin-hris-scan-result"
        >
          Last scan: {lastScan.scanned} docs checked · fired{' '}
          <span className="font-semibold text-red-700">{lastScan.fired?.expired || 0} expired</span>,{' '}
          <span className="font-semibold text-orange-700">{lastScan.fired?.expiring_7d || 0} in 7d</span>,{' '}
          <span className="font-semibold text-amber-700">{lastScan.fired?.expiring_30d || 0} in 30d</span>{' '}
          notifications.
        </div>
      )}
      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500" data-testid="admin-hris-expiring-empty">
          Nothing expiring in the next {days} days. Roster is compliant.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl">
          <ul className="divide-y divide-slate-100">
            {rows.map((row) => {
              const meta = STATUS_META[row.status] || STATUS_META.valid;
              return (
                <li key={row._id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-hris-expiring-${row._id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{row.worker_name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {row.category} · {row.subtype} · expires {fmtDate(row.expires_at)}
                    </p>
                  </div>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
                    {meta.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, cls, testid }) {
  return (
    <div className={`rounded-xl border p-3 ${cls}`} data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide font-semibold opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-0.5">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------
// Worker Lookup — legacy per-worker deep dive
// ---------------------------------------------------------------------
export function WorkerLookupTab() {
  const [workerId, setWorkerId] = useState('');
  const [profile, setProfile] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const search = useCallback(async () => {
    if (!workerId.trim()) return;
    setLoading(true);
    setProfile(null);
    try {
      const [p, a] = await Promise.all([
        apiCall(`/api/hris/admin/workers/${encodeURIComponent(workerId.trim())}/hr-profile`),
        apiCall(`/api/hris/admin/audit-events?limit=25&worker_id=${encodeURIComponent(workerId.trim())}`),
      ]);
      setProfile(p);
      setAudit(a.events || []);
    } catch (e) {
      toast.error(e?.message || 'Lookup failed');
    }
    setLoading(false);
  }, [workerId]);

  const review = async (docId, decision) => {
    setBusy(`${docId}:${decision}`);
    try {
      await apiCall(
        `/api/hris/admin/workers/${encodeURIComponent(workerId)}/documents/${encodeURIComponent(docId)}/review`,
        { method: 'POST', body: JSON.stringify({ decision, note: '' }) },
      );
      toast.success(`Document ${decision}d`);
      await search();
    } catch (e) {
      toast.error(e?.message || 'Review failed');
    }
    setBusy(null);
  };

  const grantOverride = async () => {
    const days = window.prompt('Override window (days, 1-90):', '7');
    if (!days) return;
    const justification = window.prompt('Justification (audited, min 5 chars):');
    if (!justification || justification.trim().length < 5) {
      toast.error('Justification required');
      return;
    }
    const subtype = window.prompt('Document subtype to override (leave blank for all):', '');
    setBusy('override');
    try {
      await apiCall(`/api/hris/admin/workers/${encodeURIComponent(workerId)}/override`, {
        method: 'POST',
        body: JSON.stringify({
          days: parseInt(days, 10),
          justification,
          scope: subtype ? { document_type: subtype.toUpperCase() } : {},
        }),
      });
      toast.success('Override granted');
      await search();
    } catch (e) {
      toast.error(e?.message || 'Override failed');
    }
    setBusy(null);
  };

  const revealSensitive = async (docId) => {
    if (!window.confirm('Reveal the raw storage key + file metadata? This action is audited.')) return;
    try {
      const r = await apiCall(
        `/api/hris/admin/workers/${encodeURIComponent(workerId)}/reveal-sensitive`,
        { method: 'POST', body: JSON.stringify({ document_id: docId }) },
      );
      window.alert(`storage_key: ${r?.version?.storage_key}\nfile: ${r?.version?.file_name}\nsize: ${r?.version?.size_bytes} bytes`);
    } catch (e) {
      toast.error(e?.message || 'Reveal failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-2 items-end">
        <label className="flex-1 min-w-[220px]">
          <span className="block text-xs font-semibold text-slate-600 mb-1">Worker ID</span>
          <input
            type="text"
            value={workerId}
            onChange={(e) => setWorkerId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            placeholder="Mongo ObjectId (24 hex chars)"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
            data-testid="admin-hris-worker-id"
          />
        </label>
        <button
          onClick={search}
          disabled={loading || !workerId.trim()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          data-testid="admin-hris-search"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Look up
        </button>
      </div>

      {profile && (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Worker</p>
                <p className="text-lg font-semibold text-slate-900">{profile.profile.legal_name}</p>
                <p className="text-xs text-slate-500">Status: {profile.profile.overall_status || 'unknown'}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={grantOverride}
                  disabled={busy === 'override'}
                  className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
                  data-testid="admin-hris-override"
                >
                  {busy === 'override' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5" />} Grant override
                </button>
              </div>
            </div>
            {profile.eligibility && (
              <div className={`mt-3 rounded-lg p-2 text-xs ${profile.eligibility.eligible ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
                {profile.eligibility.eligible ? 'Eligible for shifts' : `Blocked: ${(profile.eligibility.reasons || []).join('; ')}`}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-500" />
              <h3 className="font-semibold text-slate-900 text-sm">Documents</h3>
            </div>
            {profile.documents.length === 0 ? (
              <p className="p-8 text-center text-sm text-slate-500">No documents on file yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {profile.documents.map((d) => {
                  const meta = STATUS_META[d.status] || STATUS_META.pending_review;
                  return (
                    <li key={d._id} className="px-4 py-3" data-testid={`admin-hris-doc-${d._id}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">
                            {d.display_name || d.subtype}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {d.category} · {d.subtype}
                            {d.expires_at && <> · expires {fmtDate(d.expires_at)}</>}
                            <> · review: <span className="font-semibold">{d.review_state}</span></>
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
                            {meta.label}
                          </span>
                          {d.review_state !== 'approved' && (
                            <button
                              onClick={() => review(d._id, 'approve')}
                              disabled={!!busy}
                              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                              data-testid={`admin-hris-approve-${d._id}`}
                            >
                              <CheckCircle2 className="w-3 h-3" /> Approve
                            </button>
                          )}
                          {d.review_state !== 'rejected' && (
                            <button
                              onClick={() => review(d._id, 'reject')}
                              disabled={!!busy}
                              className="inline-flex items-center gap-1 rounded-md bg-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-300 disabled:opacity-50"
                              data-testid={`admin-hris-reject-${d._id}`}
                            >
                              <XCircle className="w-3 h-3" /> Reject
                            </button>
                          )}
                          <button
                            onClick={() => revealSensitive(d._id)}
                            className="inline-flex items-center gap-1 rounded-md border border-red-300 px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50"
                            data-testid={`admin-hris-reveal-${d._id}`}
                          >
                            Reveal
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {audit.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <History className="w-4 h-4 text-slate-500" />
                <h3 className="font-semibold text-slate-900 text-sm">Recent audit events for this worker</h3>
              </div>
              <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {audit.map((e, i) => (
                  <li key={i} className="px-4 py-2 text-xs">
                    <div className="flex justify-between gap-2">
                      <span className="font-mono text-slate-800">{e.action}</span>
                      <span className="text-slate-400">{fmtDate(e.created_at)}</span>
                    </div>
                    <p className="text-slate-500">by {e.actor_role || 'anon'}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Global audit log
// ---------------------------------------------------------------------
export function AuditTab() {
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const a = await apiCall('/api/hris/admin/audit-events?limit=100');
      setAudit(a.events || []);
    } catch (e) {
      toast.error(e?.message || 'Could not load audit');
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && audit.length === 0) {
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }
  if (audit.length === 0) {
    return <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500" data-testid="admin-hris-audit-empty">No audit rows yet.</div>;
  }
  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <History className="w-4 h-4 text-slate-500" />
        <h3 className="font-semibold text-slate-900 text-sm">Recent HRIS audit events ({audit.length})</h3>
      </div>
      <ul className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
        {audit.map((e, i) => (
          <li key={i} className="px-4 py-2 text-xs" data-testid={`admin-hris-audit-${i}`}>
            <div className="flex justify-between gap-2">
              <span className="font-mono text-slate-800">{e.action}</span>
              <span className="text-slate-400">{fmtDate(e.created_at)}</span>
            </div>
            <p className="text-slate-500">
              by {e.actor_role || 'anon'} · target={e.target_type} · worker={e.worker_id ? e.worker_id.slice(-8) : '—'}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
